import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import L from "leaflet";
import { Layers3, Map as MapIcon, Minus, Plus } from "lucide-react";
import type { Client } from "../types";

export type MapClient = Pick<Client, "id" | "name" | "latitude" | "longitude">;
type Props = {
  clients: MapClient[]; center: { lat: number; lng: number }; zoom: number;
  onSelect?: (client: MapClient) => void; onActivity?: () => void;
  onCameraChange?: (camera: MapCamera) => void;
  picker?: boolean; onPick?: (point: { lat: number; lng: number }) => void;
  mapLabel?: string; satelliteLabel?: string;
  markerLabelMinZoom?: number;
  optimizedTileLoading?: boolean;
  visibleClientIds?: ReadonlySet<number>; highlightedClientId?: number | null;
};
export type MapCamera = { center: { lat: number; lng: number }; zoom: number };
export type MapCanvasHandle = {
  focusClient: (client: MapClient, zoom?: number) => Promise<void>;
  resetHome: (center: { lat: number; lng: number }, zoom: number, reducedMotion: boolean) => void;
  getCamera: () => MapCamera;
  restoreCamera: (camera: MapCamera) => void;
};
type MapLayer = "roadmap" | "satellite";
type ClientMarker = {
  id: number; client: MapClient; marker: L.Marker;
  update: (client: MapClient) => void; setVisible: (visible: boolean) => void;
  setHighlighted: (highlighted: boolean) => void; setLabelVisible: (visible: boolean) => void;
  remove: () => void;
};

const MIN_ZOOM = 3;
const MAX_ZOOM = 20;
const VIEWPORT_CULL_ZOOM = 13;
const ROAD_TILES = "/map-tiles/road/{z}/{x}/{y}.png";
const SATELLITE_TILES = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ROAD_ATTRIBUTION = "&copy; OpenStreetMap contributors &copy; CARTO";
const SATELLITE_ATTRIBUTION = "Tiles &copy; Esri";

function clampZoom(value: number) {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
}

function tileUrl(url: string, optimized: boolean) {
  return optimized ? `${url}?optimized=1` : url;
}

function roadTileUrl(optimized: boolean) {
  return `${ROAD_TILES}?optimized=${optimized ? "1" : "0"}&source=carto-key-v1`;
}

function abortObsoleteTileRequests(layer: L.TileLayer | null) {
  (layer as (L.TileLayer & { _abortLoading?: () => void }) | null)?._abortLoading?.();
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#039;" })[char] || char);
}

function markerIcon(client: MapClient, labelVisible: boolean, highlighted: boolean) {
  const classes = [
    "map-marker",
    labelVisible ? "show-label" : "",
    highlighted ? "is-highlighted" : ""
  ].filter(Boolean).join(" ");
  return L.divIcon({
    className: "leaflet-heavy-marker",
    html: `<div class="${classes}" role="button" aria-label="${escapeHtml(client.name)}">${escapeHtml(client.name)}</div>`,
    iconAnchor: [19, 38]
  });
}

function makeMarker(client: MapClient, map: L.Map, labelMinZoom: number, select: () => void): ClientMarker {
  let labelVisible = map.getZoom() >= labelMinZoom;
  let highlighted = false;
  const marker = L.marker([client.latitude, client.longitude], {
    icon: markerIcon(client, labelVisible, highlighted),
    interactive: true,
    keyboard: false
  }).addTo(map);
  marker.on("click", (event) => {
    L.DomEvent.stop(event);
    select();
  });
  const entry: ClientMarker = {
    id: client.id,
    client,
    marker,
    update: (nextClient) => {
      entry.client = nextClient;
      marker.setLatLng([nextClient.latitude, nextClient.longitude]);
      marker.setIcon(markerIcon(nextClient, labelVisible, highlighted));
    },
    setVisible: (visible) => {
      const onMap = map.hasLayer(marker);
      if (visible && !onMap) marker.addTo(map);
      if (!visible && onMap) marker.removeFrom(map);
    },
    setHighlighted: (nextHighlighted) => {
      if (highlighted === nextHighlighted) return;
      highlighted = nextHighlighted;
      marker.setIcon(markerIcon(entry.client, labelVisible, highlighted));
    },
    setLabelVisible: (visible) => {
      if (labelVisible === visible) return;
      labelVisible = visible;
      marker.setIcon(markerIcon(entry.client, labelVisible, highlighted));
    },
    remove: () => {
      marker.off();
      marker.removeFrom(map);
    }
  };
  return entry;
}

const MapCanvas = forwardRef<MapCanvasHandle, Props>(function MapCanvas({
  clients, center, zoom, onSelect, onActivity, onCameraChange, picker, onPick, mapLabel = "Map", satelliteLabel = "Satellite",
  markerLabelMinZoom = 11, optimizedTileLoading = true, visibleClientIds, highlightedClientId
}, forwardedRef) {
  const host = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const canvasRenderer = useRef<L.Renderer | null>(null);
  const roadLayer = useRef<L.TileLayer | null>(null);
  const satelliteLayer = useRef<L.TileLayer | null>(null);
  const markersById = useRef(new Map<number, ClientMarker>());
  const clientsRef = useRef(clients);
  const centerRef = useRef(center);
  const currentZoomRef = useRef(clampZoom(zoom));
  const labelZoomRef = useRef(clampZoom(markerLabelMinZoom));
  const selectRef = useRef(onSelect);
  const activityRef = useRef(onActivity);
  const cameraChangeRef = useRef(onCameraChange);
  const pickRef = useRef(onPick);
  const pickerRef = useRef(picker);
  const visibleIdsRef = useRef(visibleClientIds);
  const highlightedRef = useRef(highlightedClientId);
  const optimizedTilesRef = useRef(optimizedTileLoading);
  const lastActivityAt = useRef(0);
  const [currentZoom, setCurrentZoom] = useState(clampZoom(zoom));
  const [mapLayer, setMapLayer] = useState<MapLayer>("roadmap");

  clientsRef.current = clients;
  centerRef.current = center;
  selectRef.current = onSelect;
  activityRef.current = onActivity;
  cameraChangeRef.current = onCameraChange;
  pickRef.current = onPick;
  pickerRef.current = picker;
  visibleIdsRef.current = visibleClientIds;
  highlightedRef.current = highlightedClientId;
  labelZoomRef.current = clampZoom(markerLabelMinZoom);

  const signalActivity = () => {
    const timestamp = Date.now();
    if (timestamp - lastActivityAt.current < 220) return;
    lastActivityAt.current = timestamp;
    activityRef.current?.();
  };
  const reportCamera = () => {
    const map = mapRef.current;
    if (!map) return;
    const mapCenter = map.getCenter();
    cameraChangeRef.current?.({
      center: { lat: mapCenter.lat, lng: mapCenter.lng },
      zoom: map.getZoom()
    });
  };
  const setZoomState = (nextZoom: number) => {
    const boundedZoom = clampZoom(nextZoom);
    if (currentZoomRef.current === boundedZoom) return;
    currentZoomRef.current = boundedZoom;
    setCurrentZoom(boundedZoom);
  };
  const updateMarkerLabels = (nextZoom: number) => {
    markersById.current.forEach((marker) => {
      marker.setLabelVisible(nextZoom >= labelZoomRef.current);
      marker.setHighlighted(marker.id === highlightedRef.current);
    });
  };
  const syncMarkerVisibility = () => {
    const map = mapRef.current;
    if (!map) return;
    const cullOutsideViewport = map.getZoom() >= VIEWPORT_CULL_ZOOM;
    const bufferedBounds = cullOutsideViewport ? map.getBounds().pad(.35) : null;
    markersById.current.forEach((marker) => {
      const passesFilters = !visibleIdsRef.current || visibleIdsRef.current.has(marker.id);
      const isNearby = !bufferedBounds || bufferedBounds.contains(marker.marker.getLatLng());
      marker.setVisible(passesFilters && isNearby);
    });
  };
  const syncMarkerState = () => {
    syncMarkerVisibility();
    markersById.current.forEach((marker) => {
      marker.setHighlighted(marker.id === highlightedRef.current);
      marker.setLabelVisible(currentZoomRef.current >= labelZoomRef.current);
    });
  };
  const syncMarkers = () => {
    const map = mapRef.current;
    if (!map) return;
    const nextIds = new Set(clientsRef.current.map((client) => client.id));
    markersById.current.forEach((marker, id) => {
      if (!nextIds.has(id)) {
        marker.remove();
        markersById.current.delete(id);
      }
    });
    clientsRef.current.forEach((client) => {
      const existing = markersById.current.get(client.id);
      if (existing) {
        existing.update(client);
        return;
      }
      const marker = makeMarker(client, map, labelZoomRef.current, () => {
        signalActivity();
        selectRef.current?.(marker.client);
      });
      markersById.current.set(client.id, marker);
    });
    syncMarkerState();
  };
  const clearMarkers = () => {
    markersById.current.forEach((marker) => marker.remove());
    markersById.current.clear();
  };

  useEffect(() => {
    if (!host.current || mapRef.current) return;
    const map = L.map(host.current, {
      center: [centerRef.current.lat, centerRef.current.lng],
      zoom: clampZoom(zoom),
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true,
      fadeAnimation: false,
      markerZoomAnimation: false,
      zoomAnimation: false,
      inertia: false,
      bounceAtZoomLimits: false,
      wheelDebounceTime: 90,
      wheelPxPerZoomLevel: 120,
      zoomDelta: 1,
      zoomSnap: 1
    });
    mapRef.current = map;
    canvasRenderer.current = L.canvas({ padding: .08 });
    roadLayer.current = L.tileLayer(roadTileUrl(optimizedTileLoading), {
      attribution: ROAD_ATTRIBUTION, maxZoom: MAX_ZOOM, maxNativeZoom: 20,
      updateWhenIdle: false, updateWhenZooming: false, updateInterval: 100,
      keepBuffer: optimizedTileLoading ? 2 : 4, className: "leaflet-muted-road"
    }).addTo(map);
    satelliteLayer.current = L.tileLayer(tileUrl(SATELLITE_TILES, optimizedTileLoading), {
      attribution: SATELLITE_ATTRIBUTION, maxZoom: MAX_ZOOM, maxNativeZoom: 19,
      updateWhenIdle: false, updateWhenZooming: false, updateInterval: 100,
      keepBuffer: optimizedTileLoading ? 2 : 4, className: "leaflet-satellite"
    });
    map.on("moveend", () => {
      syncMarkerVisibility();
      reportCamera();
      signalActivity();
    });
    map.on("zoomstart", () => {
      abortObsoleteTileRequests(roadLayer.current);
      abortObsoleteTileRequests(satelliteLayer.current);
    });
    map.on("zoomend", () => {
      abortObsoleteTileRequests(roadLayer.current);
      abortObsoleteTileRequests(satelliteLayer.current);
      const nextZoom = clampZoom(map.getZoom());
      setZoomState(nextZoom);
      updateMarkerLabels(nextZoom);
      syncMarkerVisibility();
      reportCamera();
      signalActivity();
    });
    map.on("click", (event: L.LeafletMouseEvent) => {
      if (pickerRef.current) pickRef.current?.({ lat: Number(event.latlng.lat.toFixed(6)), lng: Number(event.latlng.lng.toFixed(6)) });
    });
    syncMarkers();
    reportCamera();
    return () => {
      clearMarkers();
      map.remove();
      mapRef.current = null;
      roadLayer.current = null;
      satelliteLayer.current = null;
      canvasRenderer.current = null;
    };
  }, []);

  useEffect(() => { syncMarkers(); }, [clients]);
  useEffect(() => {
    if (optimizedTilesRef.current === optimizedTileLoading) return;
    optimizedTilesRef.current = optimizedTileLoading;
    const buffer = optimizedTileLoading ? 2 : 4;
    if (roadLayer.current) {
      roadLayer.current.options.keepBuffer = buffer;
      roadLayer.current.setUrl(roadTileUrl(optimizedTileLoading));
    }
    if (satelliteLayer.current) {
      satelliteLayer.current.options.keepBuffer = buffer;
      satelliteLayer.current.setUrl(tileUrl(SATELLITE_TILES, optimizedTileLoading));
    }
  }, [optimizedTileLoading]);
  useEffect(() => { updateMarkerLabels(currentZoom); }, [currentZoom]);
  useEffect(() => { syncMarkerVisibility(); }, [visibleClientIds]);
  useEffect(() => { updateMarkerLabels(currentZoomRef.current); }, [markerLabelMinZoom]);
  useEffect(() => {
    markersById.current.forEach((marker) => marker.setHighlighted(marker.id === highlightedClientId));
  }, [highlightedClientId]);
  useEffect(() => {
    const map = mapRef.current;
    const nextZoom = clampZoom(zoom);
    setZoomState(nextZoom);
    if (map) map.setView([center.lat, center.lng], nextZoom, { animate: false });
  }, [center.lat, center.lng, zoom]);

  useImperativeHandle(forwardedRef, () => ({
    focusClient: (client, targetZoom = Math.max(12, currentZoomRef.current)) => new Promise<void>((resolve) => {
      const map = mapRef.current;
      const nextZoom = clampZoom(targetZoom);
      setZoomState(nextZoom);
      if (!map) {
        window.setTimeout(resolve, 120);
        return;
      }
      map.setView([client.latitude, client.longitude], nextZoom, { animate: false });
      window.setTimeout(resolve, 100);
    }),
    resetHome: (nextCenter, nextZoom) => {
      const boundedZoom = clampZoom(nextZoom);
      setZoomState(boundedZoom);
      mapRef.current?.setView([nextCenter.lat, nextCenter.lng], boundedZoom, { animate: false });
    },
    getCamera: () => {
      const map = mapRef.current;
      const mapCenter = map?.getCenter();
      return {
        center: mapCenter ? { lat: mapCenter.lat, lng: mapCenter.lng } : { ...centerRef.current },
        zoom: map?.getZoom() ?? currentZoomRef.current
      };
    },
    restoreCamera: (camera) => {
      const boundedZoom = clampZoom(camera.zoom);
      setZoomState(boundedZoom);
      mapRef.current?.setView([camera.center.lat, camera.center.lng], boundedZoom, { animate: false });
    }
  }), []);

  const changeZoom = (amount: number) => {
    const next = clampZoom(currentZoomRef.current + amount);
    setZoomState(next);
    mapRef.current?.setZoom(next, { animate: false });
    signalActivity();
  };
  const changeLayer = (next: MapLayer) => {
    if (mapLayer === next) return;
    const map = mapRef.current;
    setMapLayer(next);
    if (map && roadLayer.current && satelliteLayer.current) {
      if (next === "satellite") {
        if (map.hasLayer(roadLayer.current)) map.removeLayer(roadLayer.current);
        if (!map.hasLayer(satelliteLayer.current)) map.addLayer(satelliteLayer.current);
      } else {
        if (map.hasLayer(satelliteLayer.current)) map.removeLayer(satelliteLayer.current);
        if (!map.hasLayer(roadLayer.current)) map.addLayer(roadLayer.current);
      }
    }
    signalActivity();
  };

  return <div className={"map-shell map-layer-" + mapLayer + (picker ? " map-picker" : "")}>
    <div ref={host} className="google-map leaflet-map-host" />
    {picker && <div className="fallback-label">Tap the map to choose a location</div>}
    <div className="map-control-bar" onClick={(event) => event.stopPropagation()}>
      <div className="layer-switch" aria-label="Map layer">
        <button className={mapLayer === "roadmap" ? "active" : ""} onClick={() => changeLayer("roadmap")}
          aria-pressed={mapLayer === "roadmap"}><MapIcon /> {mapLabel}</button>
        <button className={mapLayer === "satellite" ? "active" : ""} onClick={() => changeLayer("satellite")}
          aria-pressed={mapLayer === "satellite"}><Layers3 /> {satelliteLabel}</button>
      </div>
      <span className="control-divider" />
      <div className="custom-zoom">
        <button onClick={() => changeZoom(-1)} aria-label="Zoom out" disabled={currentZoom <= MIN_ZOOM}><Minus /></button>
        <button onClick={() => changeZoom(1)} aria-label="Zoom in" disabled={currentZoom >= MAX_ZOOM}><Plus /></button>
      </div>
    </div>
  </div>;
});

export default MapCanvas;
